import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  /**
   * HALI: Merkt, ob gerade das fixe Hoverbild-Overlay sichtbar ist. Wenn ja, darf
   * resetImage() nicht im Slideshow-Stack zurückblättern.
   * @type {boolean}
   */
  #hoverImageActive = false;

  /**
   * HALI: Bilddaten des fixen Hoverbilds für die aktuell gewählte Variante.
   * Ist ein Swatch aktiv, zählt ausschließlich dessen Metafeld — sonst würde bei einer
   * Variante ohne Hoverbild fälschlich das der Startvariante stehenbleiben.
   * @returns {{src?: string, srcset?: string}}
   */
  #getHoverImageSource() {
    const selectedOption = this.variantPicker?.selectedOption;

    if (selectedOption) {
      return {
        src: selectedOption.dataset.optionHoverImageSrc,
        srcset: selectedOption.dataset.optionHoverImageSrcset,
      };
    }

    const { hoverImage } = this.refs;
    return { src: hoverImage?.dataset.src, srcset: hoverImage?.dataset.srcset };
  }

  /**
   * HALI: Blendet das fixe Hoverbild ein. src/srcset werden erst hier gesetzt, damit im
   * Grid nicht pro Karte ein zusätzliches Bild geladen wird.
   * @returns {boolean} true, wenn ein Hoverbild hinterlegt ist und gezeigt wird.
   */
  #showHoverImage() {
    const { hoverImage } = this.refs;
    if (!(hoverImage instanceof HTMLImageElement)) return false;

    const { src, srcset } = this.#getHoverImageSource();
    if (!src) return false;

    if (hoverImage.getAttribute('src') !== src) {
      hoverImage.setAttribute('src', src);

      if (srcset) {
        hoverImage.setAttribute('srcset', srcset);
      } else {
        hoverImage.removeAttribute('srcset');
      }
    }

    this.#hoverImageActive = true;

    if (hoverImage.complete) {
      hoverImage.classList.add('card-gallery__hover-image--visible');
    } else {
      // Erst nach dem Laden einblenden, sonst blitzt beim ersten Hover ein leeres Bild auf.
      // Der Guard verhindert, dass es aufpoppt, wenn die Maus vorher schon weg ist.
      hoverImage.addEventListener(
        'load',
        () => {
          if (this.#hoverImageActive) hoverImage.classList.add('card-gallery__hover-image--visible');
        },
        { once: true }
      );
    }

    return true;
  }

  /**
   * HALI: Blendet das fixe Hoverbild wieder aus.
   */
  #hideHoverImage() {
    this.#hoverImageActive = false;
    this.refs.hoverImage?.classList.remove('card-gallery__hover-image--visible');
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    // HALI-Karten haben nur einen priceContainer; Stock-Horizon ggf. zwei (Quick-Add).
    // Fallback auf [0] sicherstellt, dass beide Layouts funktionieren.
    const priceContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
    const priceContainer = priceContainers[1] ?? priceContainers[0];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * HALI: Stellt sicher, dass für ein Varianten-Medium ein Slide in der Card-Gallery existiert.
   *
   * card-gallery.liquid rendert versteckte Varianten-Slides nicht mehr serverseitig (PageSpeed: ~30 Slides
   * pro Karte). Fehlt der Slide, wird er hier aus den data-option-media-*-Attributen des Swatch-Inputs
   * (variant-swatches.liquid) aufgebaut — Markup 1:1 wie slideshow-slide.liquid + product-media.liquid:
   * hidden + variant-image, loading="lazy", sizes "auto, …". Danach greift die bestehende Logik
   * (slideshow.select → reveal) unverändert.
   *
   * Einfügeposition: hinter dem letzten vorhandenen Varianten-Slide, sonst hinter dem ersten Slide —
   * so bleibt refs.slides[0] (= initialSlide) stabil und die Reihenfolge entspricht der serverseitigen
   * (Varianten-Bilder vor den generischen Produktbildern).
   *
   * @param {string | undefined | null} mediaId - Die Media-ID des Varianten-Bildes.
   * @returns {Promise<HTMLElement | undefined>} Der vorhandene oder neu erzeugte Slide.
   */
  async #ensureVariantSlide(mediaId) {
    const { slideshow, cardGallery } = this.refs;
    if (!slideshow || !mediaId) return;

    const existing = slideshow.refs.slides?.find((slide) => slide.getAttribute('slide-id') == mediaId);
    if (existing) return existing;

    const input = this.querySelector(`input[data-option-media-id="${mediaId}"][data-option-media-src]`);
    const { scroller } = slideshow.refs;
    if (!(input instanceof HTMLInputElement) || !(scroller instanceof HTMLElement) || !(cardGallery instanceof HTMLElement)) {
      return;
    }

    const src = input.dataset.optionMediaSrc;
    if (!src) return;

    const alt = input.dataset.optionMediaAlt ?? '';
    const ratio = input.dataset.optionMediaRatio || '1.0';
    const mediaType = input.dataset.optionMediaType || 'image';
    const focalPoint = input.dataset.optionMediaFocal ?? '';
    const naturalWidth = parseInt(input.dataset.optionMediaWidth ?? '', 10) || 3840;
    const naturalHeight = parseInt(input.dataset.optionMediaHeight ?? '', 10) || naturalWidth;
    const imageSizes = cardGallery.dataset.imageSizes || '(min-width: 750px) 50vw, 100vw';
    const mediaFit = cardGallery.dataset.mediaFit || 'cover';

    const slides = slideshow.refs.slides ?? [];

    // Eindeutiger --slide-N-Index (serverseitig = forloop.index0): nächster freier Wert.
    const usedIndexes = slides
      .map((slide) => parseInt(slide.style.getPropertyValue('--slideshow-timeline').match(/--slide-(\d+)/)?.[1] ?? '', 10))
      .filter((index) => !Number.isNaN(index));
    const timelineIndex = usedIndexes.length ? Math.max(...usedIndexes) + 1 : 0;

    const slide = document.createElement('slideshow-slide');
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', 'true');
    slide.setAttribute(
      'style',
      `--slideshow-timeline: --slide-${timelineIndex}; --product-media-fit: ${mediaFit}; --grid-template-rows: 50dvh 1fr; --grid-template-rows-desktop: 70dvh 1fr;`
    );
    slide.className = `product-media-container media-fit product-media-container--${mediaType}${
      cardGallery.hasAttribute('data-constrain-height') ? ' constrain-height' : ''
    }`;
    slide.setAttribute('variant-image', '');
    slide.setAttribute('slide-id', mediaId);
    slide.hidden = true;

    const media = document.createElement('div');
    media.className = 'product-media';
    media.setAttribute('style', `--ratio: ${ratio}`);
    media.setAttribute('data-media-id', mediaId);

    const urlForWidth = (/** @type {number} */ width) => src.replace(/([?&])width=\d+/, `$1width=${width}`);
    const widths = [240, 352, 832, 1200, 1600, 1920, 2560, 3840].filter((width) => width <= naturalWidth);
    const renderedWidth = Math.min(3840, naturalWidth);
    const renderedHeight = Math.round((renderedWidth * naturalHeight) / naturalWidth);

    const image = document.createElement('img');
    // loading="lazy" MUSS vor src gesetzt werden, sonst lädt der Browser das Bild sofort (auch detached).
    image.setAttribute('loading', 'lazy');
    image.setAttribute('src', src);
    image.setAttribute('alt', alt);
    image.setAttribute('srcset', widths.map((width) => `${urlForWidth(width)} ${width}w`).join(', '));
    image.setAttribute('width', `${renderedWidth}`);
    image.setAttribute('height', `${renderedHeight}`);
    image.setAttribute('sizes', `auto, ${imageSizes}`);
    image.className = 'product-media__image';
    image.setAttribute('data_max_resolution', src);
    image.setAttribute('fetchpriority', 'auto');
    image.setAttribute('style', `--focal-point: ${focalPoint};`);
    if (this.productTransitionEnabled) image.setAttribute('ref', 'imagesToTransition[]');

    media.append(image);
    slide.append(media);

    const anchor = [...slides].reverse().find((candidate) => candidate.hasAttribute('variant-image')) ?? slides[0];
    if (anchor) {
      anchor.after(slide);
    } else {
      scroller.append(slide);
    }

    slideshow.observeSlide?.(slide);

    // refs (Component) werden per MutationObserver im nächsten Microtask nachgezogen.
    await Promise.resolve();
    if (!slideshow.refs.slides?.includes(slide)) {
      await new Promise((resolve) => setTimeout(resolve));
    }

    return slide;
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  async #updateVariantImages() {
    // HALI: Beim Variantenwechsel darf kein Hoverbild der alten Variante stehenbleiben.
    this.#hideHoverImage();

    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      // HALI: Slide der gewählten Variante ggf. erst erzeugen (siehe #ensureVariantSlide).
      await this.#ensureVariantSlide(selectedImageId);

      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  async previewVariant(id) {
    // HALI: Hover-Vorschau nur auf Desktop. Auf Mobile/Touch sendet manche
    // Hardware pointerenter als 'mouse' → sonst würde die Karte beim Scrollen
    // ungewollt das Bild wechseln.
    if (!isDesktopBreakpoint()) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    // HALI: Varianten-Slide ggf. erst erzeugen (siehe #ensureVariantSlide).
    await this.#ensureVariantSlide(id);
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    // HALI: Hover-Vorschau (zeigt nächstes/Parent-Bild) nur auf Desktop.
    // Auf Mobile bleibt das Variantenbild stehen.
    if (!isDesktopBreakpoint()) return;
    if (event.pointerType !== 'mouse') return;

    // HALI: Ist an der Variante ein fixes Hoverbild hinterlegt (Metafeld custom.hoverbild),
    // gewinnt das Overlay — dann wird NICHT im Slideshow-Stack weitergeblättert.
    if (this.#showHoverImage()) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    // HALI: Reset gehört zur Hover-Vorschau → auf Mobile deaktiviert.
    if (!isDesktopBreakpoint()) return;
    if (event.pointerType !== 'mouse') return;

    // HALI: War das fixe Hoverbild sichtbar, wurde die Slideshow nie bewegt — dann darf
    // hier auch nicht zurückgeblättert werden, sonst zeigt die Karte ein falsches Bild.
    if (this.#hoverImageActive) {
      this.#hideHoverImage();
      return;
    }

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = async () => {
    // HALI: Reset gehört zur Hover-Vorschau → auf Mobile deaktiviert.
    if (!isDesktopBreakpoint()) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        // HALI: Varianten-Slide ggf. erst erzeugen (siehe #ensureVariantSlide).
        await this.#ensureVariantSlide(id);
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
